import { components as c } from "@/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTrackerQuery } from "@/features/trackers/api/selectTracker";
import { useUpdateTracker } from "@/features/trackers/api/updateTracker";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import {
    buildSharedAttributesFromDiary,
    formatSharedAttributeDate,
    getEventTypeLabel,
    type SharedAttributeOption,
} from "@/features/shared_attributes/components/SharedAttributeDisplay";
import { NextButton } from "@/features/i18n/formComponents";
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@patient-watch/ui/radio-group";
import { Label } from "@patient-watch/ui/label";

// Generate a conversational description for linking to an intervention
const getInterventionDescription = (sa: SharedAttributeOption): string => {
    if (sa.events.length === 0) {
        // No events, just other trackers
        if (sa.trackers.length > 0) {
            return `when the ${sa.trackers[0]?.form_name || "questionnaire"} started`;
        }
        return "";
    }

    const event = sa.events[0];
    if (!event) {
        return "";
    }
    const eventType = getEventTypeLabel(event.event_type);

    // Build conversational description like "the date of the left foot injection"
    if (event.details) {
        return `the date of the ${event.details.toLowerCase()} ${eventType}`;
    }

    return `the date of the ${eventType}`;
};

const TrackerDetailsForm = () => {
    const { diaryId, trackerId } = Route.useParams();
    const { data: tracker, isLoading: trackerLoading } = useTrackerQuery(trackerId);
    const { data: diary, isLoading: diaryLoading } = useDiaryByIdQuery(diaryId);
    const updateTracker = useUpdateTracker({ diaryId });
    const navigate = useNavigate();

    // Selection state: 'new' means create new date, otherwise it's a shared_attribute_id
    const [selectedOption, setSelectedOption] = useState<string | null>(null);

    if (trackerLoading || diaryLoading || !tracker || !diary) {
        return <div>Loading...</div>;
    }

    // Build shared attribute options from diary, excluding the current tracker
    const sharedAttributes = buildSharedAttributesFromDiary(diary, trackerId);
    const currentSharedAttributeId = tracker.shared_attributes?.shared_attribute_id;

    // Set default selection to the tracker's current shared attribute if it has linked items
    if (selectedOption === null && currentSharedAttributeId) {
        const currentSa = sharedAttributes.find((sa) => sa.shared_attribute_id === currentSharedAttributeId);
        if (currentSa && (currentSa.trackers.length > 0 || currentSa.events.length > 0)) {
            setSelectedOption(currentSharedAttributeId);
        }
    }

    const handleContinue = () => {
        if (selectedOption === "new") {
            // Navigate to date picker page
            void navigate({
                to: "/templates/diary/$diaryId/trackers/$trackerId/date",
                params: { diaryId, trackerId },
            });
        } else if (selectedOption) {
            // Link to existing shared attribute
            updateTracker.mutate({
                tracker_id: trackerId,
                shared_attribute_id: selectedOption,
            });
            void navigate({ to: `/patients/${diaryId}` });
        }
    };

    // Get the questionnaire name for display
    const questionnaireName = tracker.form?.form_name || "questionnaire";

    return (
        <div className="space-y-6">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Record Outcomes</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <p className="mb-6 text-base">
                        Do you want to record <strong>{questionnaireName}</strong> outcomes from:
                    </p>

                    <RadioGroup value={selectedOption || ""} onValueChange={setSelectedOption} className="space-y-3">
                        {/* Existing shared attributes - prioritize those with interventions */}
                        {sharedAttributes
                            .sort((a, b) => {
                                // Sort interventions first
                                const aHasEvents = a.events.length > 0 ? 1 : 0;
                                const bHasEvents = b.events.length > 0 ? 1 : 0;
                                return bHasEvents - aHasEvents;
                            })
                            .map((sa) => {
                                const isCurrent = sa.shared_attribute_id === currentSharedAttributeId;
                                const date = formatSharedAttributeDate(sa);
                                const description = getInterventionDescription(sa);

                                return (
                                    <div
                                        key={sa.shared_attribute_id}
                                        className={`flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors ${
                                            selectedOption === sa.shared_attribute_id
                                                ? "border-primary bg-primary/5"
                                                : ""
                                        }`}
                                    >
                                        <RadioGroupItem
                                            value={sa.shared_attribute_id}
                                            id={sa.shared_attribute_id}
                                            className="mt-0.5"
                                        />
                                        <Label htmlFor={sa.shared_attribute_id} className="flex-1 cursor-pointer">
                                            <div className="space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="font-semibold text-base">{date}</span>
                                                    {isCurrent && (
                                                        <span className="bg-primary/10 px-2 py-0.5 rounded text-primary text-xs">
                                                            Current
                                                        </span>
                                                    )}
                                                </div>
                                                {description && (
                                                    <p className="text-muted-foreground text-sm">{description}</p>
                                                )}
                                            </div>
                                        </Label>
                                    </div>
                                );
                            })}

                        {/* Option to create new */}
                        <div
                            className={`flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors ${
                                selectedOption === "new" ? "border-primary bg-primary/5" : ""
                            }`}
                        >
                            <RadioGroupItem value="new" id="new-schedule" className="mt-0.5" />
                            <Label htmlFor="new-schedule" className="flex-1 cursor-pointer">
                                <span className="font-semibold text-base">A different date</span>
                            </Label>
                        </div>
                    </RadioGroup>
                </c.ui.CardContent>
            </c.ui.Card>
            <NextButton onClick={handleContinue} disabled={!selectedOption} />
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/trackers/$trackerId/details")({
    component: TrackerDetailsForm,
});
